import { ReactElement } from "react";
import {
	IntegrationFlowValidationResultPlanningSampleModel,
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { DateString, FormItem } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { IInputAttachable, IObjectInputAttachable } from "client/Forms/attachable";
import { isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { PlanningMappingStepConfigurationFormData, ResultMapperStyledFieldFormData } from "./common";
import { ResultMapperFormResult } from "./ResultMapperFormResult";
import { ExpressionTextInput } from "./ExpressionTextInput";
import { GeneralResultFieldWithColorActions } from "./GeneralResultFieldWithColorAction";
import { ColorSelector } from "./ColorSelector";

export type PlanningFormProps = {
	attachable: IObjectInputAttachable<PlanningMappingStepConfigurationFormData>;
	planningSample: OptionalModel<IntegrationFlowValidationResultPlanningSampleModel> | null;
	isReadOnly: boolean;
	timeZone: TimeZone;
};
export const PlanningForm = (props: PlanningFormProps): ReactElement => {
	const {
		attachable,
		planningSample,
		isReadOnly,
		timeZone
	} = props;

	const linkExpressionHandler: IInputAttachable<string> = attachable.for("linkExpression");
	const titleHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("title");
	const startDateTimeHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("startDateTime");
	const endDateTimeHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("endDateTime");
	const subtitle1Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle1");
	const subtitle2Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle2");
	const subtitle3Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle3");
	const descriptionHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("description");
	const detail1Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("detail1");
	const detail2Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("detail2");
	const detail3Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("detail3");
	const detail4Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("detail4");
	const detail5Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("detail5");
	const hasResult: boolean = isNotNil(planningSample) && planningSample.isSet;

	const planningSampleValue: IntegrationFlowValidationResultPlanningSampleModel | null = isNotNil(planningSample)
		&& planningSample.isSet
			? planningSample.value
			: null;

	return (
		<>
			<FormItem
				label={localize("_Title")}
				isRequired
			>
				<GeneralResultFieldWithColorActions
					attachable={titleHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.title}
				/>
			</FormItem>
			{titleHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.title
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_StartDateTime")}
				isRequired
			>
				<GeneralResultFieldWithColorActions
					attachable={startDateTimeHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.startDateTime}
				>
					{planningSampleValue?.startDateTime?.value && (
						<DateString
							value={planningSampleValue.startDateTime.value}
							timezone={timeZone}
							format="DMYT"
						/>
					)}
				</GeneralResultFieldWithColorActions>
			</FormItem>
			{startDateTimeHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.startDateTime
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_EndDateTime")}
				isRequired
			>
				<GeneralResultFieldWithColorActions
					attachable={endDateTimeHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.endDateTime}
				>
					{planningSampleValue?.endDateTime?.value && (
						<DateString
							value={planningSampleValue.endDateTime.value}
							timezone={timeZone}
							format="DMYT"
						/>
					)}
				</GeneralResultFieldWithColorActions>
			</FormItem>
			{endDateTimeHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.endDateTime
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_Link")}
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={linkExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={linkExpressionHandler}
					hasResult={hasResult}
					resultValue={planningSampleValue?.link}
				/>
			</FormItem>
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "1"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={subtitle1Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.subTitle1}
				/>
			</FormItem>
			{subtitle1Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.subTitle1
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "2"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={subtitle2Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.subTitle2}
				/>
			</FormItem>
			{subtitle2Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.subTitle2
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "3"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={subtitle3Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.subTitle3}
				/>
			</FormItem>
			{subtitle3Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.subTitle3
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_Description")}
			>
				<GeneralResultFieldWithColorActions
					attachable={descriptionHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.description}
				/>
			</FormItem>
			{descriptionHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.description
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_DetailN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "1"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={detail1Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.detail1}
				/>
			</FormItem>
			{detail1Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.detail1
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_DetailN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "2"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={detail2Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.detail2}
				/>
			</FormItem>
			{detail2Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.detail2
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_DetailN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "3"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={detail3Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.detail3}
				/>
			</FormItem>
			{detail3Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.detail3
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_DetailN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "4"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={detail4Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.detail4}
				/>
			</FormItem>
			{detail4Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.detail4
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_DetailN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "5"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={detail5Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={planningSampleValue?.detail5}
				/>
			</FormItem>
			{detail5Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									planningSampleValue?.detail5
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
		</>
	);
};
PlanningForm.displayName = "PlanningForm";
